import type { StatEvent } from './types'

/**
 * Merging one tap's two versions.
 *
 * The brief's rule is "conflicts resolve by event ID, never by overwriting", and
 * this is where the client honours it. The same three rules the database applies
 * in `push_stat_events`, deliberately duplicated rather than shared, because the
 * two sides genuinely are separate systems and the client has to reach the same
 * answer offline:
 *
 *   1. identity is the event id -- the same tap seen twice is one tap;
 *   2. the later `updatedAt` wins the mutable fields;
 *   3. `deleted` only ever goes false -> true.
 *
 * Rule 3 is the one that matters. Without it a stale copy arriving late from a
 * device that had been offline resurrects a tap somebody deliberately undid, and
 * the total on screen is a number nobody entered. Losing an undo is worse than
 * losing a tap: a missing tap is visible to the scorer, a resurrected one is not.
 */
export function mergeEvent(local: StatEvent, remote: StatEvent): StatEvent {
  const remoteIsNewer = remote.updatedAt > local.updatedAt
  return {
    ...local,
    playerId: remoteIsNewer ? remote.playerId : local.playerId,
    statType: remoteIsNewer ? remote.statType : local.statType,
    period: remoteIsNewer ? remote.period : local.period,
    deleted: local.deleted || remote.deleted,
    updatedAt: Math.max(local.updatedAt, remote.updatedAt),
    syncedAt: maxOrNull(local.syncedAt, remote.syncedAt),
  }
}

/** True when the merge would actually change the stored row. */
export function mergeChanges(local: StatEvent, merged: StatEvent): boolean {
  return (
    merged.deleted !== local.deleted ||
    merged.playerId !== local.playerId ||
    merged.statType !== local.statType ||
    merged.period !== local.period ||
    merged.updatedAt !== local.updatedAt ||
    (merged.syncedAt ?? null) !== (local.syncedAt ?? null)
  )
}

/** Everything the server has not acknowledged at its current version. */
export function outbox(events: StatEvent[]): StatEvent[] {
  return events.filter((e) => e.syncedAt == null || e.syncedAt < e.updatedAt)
}

function maxOrNull(a: number | null | undefined, b: number | null | undefined): number | null {
  if (a == null) return b ?? null
  if (b == null) return a
  return Math.max(a, b)
}
